import React, { useState, useEffect } from 'react';
import './AFoto.css';
import API from '../../api/API';
import TokenService from '../../api/tokenService';
import { BASE_URL } from '../../api/ApiBase';

const AFoto = () => {
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      setEdizioneSelezionata(edizione);
      loadPendingPhotos(edizione.id);
    } else {
      setLoading(false);
    }
  }, []);

  const loadPendingPhotos = async (edizioneId) => {
    try {
      setLoading(true);
      // For now, using the existing foto API and filtering for pending photos
      // This would need to be updated with a proper admin endpoint
      const albumResp = await API.foto.edizione(edizioneId);
      if(!albumResp.isSuccess && albumResp.data.length > 0)
        return;

      const response = await API.foto.pending(albumResp.data[0].id);
      if (response.isSuccess) {
        // response.data contains a flat list of photos
        const allPhotos = response.data.map(foto => {
          console.log(foto);
          return {
            id: foto.id,
            albumId: foto.album,
            albumName: albumResp.data[0].nome || 'Album',
            filename: foto.nome,
            url: `${BASE_URL}/foto/thumbnail/${foto.album}/${foto.nome}`,
            fullUrl: `${BASE_URL}/foto/image/${foto.album}/${foto.nome}`,
            status: foto.approve ? 'approved' : 'pending',
            uploadDate: foto.upload || new Date().toISOString()
          };
        });
        setPendingPhotos(allPhotos);
      }
    } catch (error) {
      console.error('Error loading pending photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPhoto = (photoId) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
    setSelectAllChecked(newSelected.size === pendingPhotos.length);
  };

  const handleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedPhotos(new Set());
      setSelectAllChecked(false);
    } else {
      setSelectedPhotos(new Set(pendingPhotos.map(photo => photo.id)));
      setSelectAllChecked(true);
    }
  };

  const handleBulkAction = (action) => {
    if (selectedPhotos.size === 0) return;
    setModalAction(action);
    setShowConfirmModal(true);
  };

  const confirmBulkAction = async () => {
    try {
      // Convert photo IDs from string format "albumId_photoId" to integer photoId
      const photoIds = Array.from(selectedPhotos).map(photoId => {
        // Find the photo object to get the actual numeric ID
        const photo = pendingPhotos.find(p => p.id === photoId);
        return photo ? photo.id : parseInt(photoId.split('_')[1] || photoId);
      });
      
      if (modalAction === 'approve') {
        console.log('Approving photos:', photoIds);
        const response = await API.foto.approve(photoIds);
        
        if (response.isSuccess) {
          // Remove approved photos from pending list
          setPendingPhotos(prev => prev.filter(photo => !selectedPhotos.has(photo.id)));
        } else {
          console.error('Failed to approve photos:', response);
          alert('Errore durante l\'approvazione delle foto');
          return;
        }
      } else if (modalAction === 'delete') {
        console.log('Rejecting photos:', photoIds);
        const response = await API.foto.reject(photoIds);
        
        if (response.isSuccess) {
          // Remove rejected photos from pending list
          setPendingPhotos(prev => prev.filter(photo => !selectedPhotos.has(photo.id)));
        } else {
          console.error('Failed to reject photos:', response);
          alert('Errore durante il rifiuto delle foto');
          return;
        }
      }

      setSelectedPhotos(new Set());
      setSelectAllChecked(false);
      setShowConfirmModal(false);
      setModalAction(null);
    } catch (error) {
      console.error(`Error ${modalAction}ing photos:`, error);
      alert(`Errore durante l'operazione: ${error.message}`);
    }
  };

  const handleSingleAction = async (photoId, action) => {
    try {
      // Get the actual numeric ID from the photo object
      const photo = pendingPhotos.find(p => p.id === photoId);
      const numericId = photo ? photo.id : parseInt(photoId.split('_')[1] || photoId);
      
      if (action === 'approve') {
        console.log('Approving photo:', numericId);
        const response = await API.foto.approve([numericId]);
        
        if (!response.isSuccess) {
          console.error('Failed to approve photo:', response);
          alert('Errore durante l\'approvazione della foto');
          return;
        }
      } else if (action === 'delete') {
        console.log('Rejecting photo:', numericId);
        const response = await API.foto.reject([numericId]);
        
        if (!response.isSuccess) {
          console.error('Failed to reject photo:', response);
          alert('Errore durante il rifiuto della foto');
          return;
        }
      }

      // Remove photo from pending list
      setPendingPhotos(prev => prev.filter(photo => photo.id !== photoId));
      setSelectedPhotos(prev => {
        const newSelected = new Set(prev);
        newSelected.delete(photoId);
        return newSelected;
      });
    } catch (error) {
      console.error(`Error ${action}ing photo:`, error);
      alert(`Errore durante l'operazione: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="afoto-container">
        <div className="afoto-loading">
          <div className="afoto-spinner"></div>
          <p>Caricamento foto in attesa di approvazione...</p>
        </div>
      </div>
    );
  }

  if (!edizioneSelezionata) {
    return (
      <div className="afoto-container">
        <div className="afoto-no-edition">
          <h2>Nessuna edizione selezionata</h2>
          <p>Seleziona un'edizione per gestire le foto.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="afoto-container">
      <div className="afoto-header">
        <h1>Gestione Foto</h1>
        <p className="afoto-subtitle">
          {pendingPhotos.length} foto in attesa di approvazione
        </p>
      </div>

      {pendingPhotos.length > 0 && (
        <div className="afoto-controls">
          <div className="afoto-select-all">
            <label className="afoto-checkbox-label">
              <input
                type="checkbox"
                checked={selectAllChecked}
                onChange={handleSelectAll}
                className="afoto-checkbox"
              />
              <span className="afoto-checkmark"></span>
              Seleziona tutto ({selectedPhotos.size}/{pendingPhotos.length})
            </label>
          </div>

          {selectedPhotos.size > 0 && (
            <div className="afoto-bulk-actions">
              <button
                className="afoto-btn afoto-btn-approve"
                onClick={() => handleBulkAction('approve')}
              >
                <i className="fas fa-check"></i>
                Approva selezionate ({selectedPhotos.size})
              </button>
              <button
                className="afoto-btn afoto-btn-delete"
                onClick={() => handleBulkAction('delete')}
              >
                <i className="fas fa-trash"></i>
                Elimina selezionate ({selectedPhotos.size})
              </button>
            </div>
          )}
        </div>
      )}

      {pendingPhotos.length === 0 ? (
        <div className="afoto-empty">
          <i className="fas fa-images afoto-empty-icon"></i>
          <h3>Nessuna foto in attesa</h3>
          <p>Tutte le foto sono state approvate o non ci sono nuove foto da approvare.</p>
        </div>
      ) : (
        <div className="afoto-grid">
          {pendingPhotos.map((photo) => (
            <div key={photo.id} className="afoto-photo-card">
              <div className="afoto-photo-selector">
                <label className="afoto-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedPhotos.has(photo.id)}
                    onChange={() => handleSelectPhoto(photo.id)}
                    className="afoto-checkbox"
                  />
                  <span className="afoto-checkmark"></span>
                </label>
              </div>
              
              <div className="afoto-photo-image">
                <img
                  src={photo.url}
                  alt={`Foto da ${photo.albumName}`}
                  loading="lazy"
                  onClick={() => window.open(photo.fullUrl, '_blank')}
                />
              </div>

              <div className="afoto-photo-info">
                <h4>{photo.albumName}</h4>
                <p className="afoto-filename">{photo.filename}</p>
                <p className="afoto-upload-date">
                  Caricata il {photo.uploadDate ? new Date(photo.uploadDate).toLocaleDateString('it-IT') : 'Data non disponibile'}
                </p>
              </div>

              <div className="afoto-photo-actions">
                <button
                  className="afoto-btn afoto-btn-approve afoto-btn-small"
                  onClick={() => handleSingleAction(photo.id, 'approve')}
                  title="Approva foto"
                >
                  <i className="fas fa-check"></i>
                </button>
                <button
                  className="afoto-btn afoto-btn-delete afoto-btn-small"
                  onClick={() => handleSingleAction(photo.id, 'delete')}
                  title="Elimina foto"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="afoto-modal-overlay">
          <div className="afoto-modal">
            <h3>Conferma azione</h3>
            <p>
              Sei sicuro di voler {modalAction === 'approve' ? 'approvare' : 'eliminare'} {' '}
              {selectedPhotos.size} foto selezionata{selectedPhotos.size > 1 ? 'e' : ''}?
            </p>
            <div className="afoto-modal-actions">
              <button
                className="afoto-btn afoto-btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Annulla
              </button>
              <button
                className={`afoto-btn ${modalAction === 'approve' ? 'afoto-btn-approve' : 'afoto-btn-delete'}`}
                onClick={confirmBulkAction}
              >
                {modalAction === 'approve' ? 'Approva' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AFoto;